import "simplebar-react/dist/simplebar.min.css";
import "swiper/css";
import "swiper/css/navigation";
import "src/styles/shadcn-theme.css";

import React from "react";

import { datadogRum } from "@datadog/browser-rum";
import { LicenseInfo } from "@mui/x-license";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import ErrorBoundary from "src/components/common/ErrorBoundary";

import App from "./App";
import { SettingsProvider } from "./contexts/SettingsContext";
import { UserProvider } from "./contexts/UserContext";

const container = document.getElementById("root");
if (container === null) throw new Error("No root");
const root = createRoot(container);

if (import.meta.env.VITE_ENVIRONMENT !== "local") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
    // Performance Monitoring
    tracesSampleRate: 0.1,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.VITE_ENVIRONMENT,
  });
}

if (
  ["production", "staging", "demo"].includes(import.meta.env.VITE_ENVIRONMENT)
) {
  const defaultPrivacyLevel = "allow";
  datadogRum.init({
    applicationId: "7897a9ed-18e1-465e-a749-4c8d215b3a90",
    clientToken: "pub9a70fb276f196b54d9f25f543efff77f",
    site: "datadoghq.com",
    service: "app-frontend",
    env: import.meta.env.VITE_ENVIRONMENT,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [import.meta.env.VITE_API_URL],
    defaultPrivacyLevel,
  });
  posthog.init("phc_qn5D4TBepQ3cdu0N8smR8BKeLlUfpufBzSBLGU1vtUS", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "always",
  });
}
LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

import { queryClient } from "src/lib/queryClient";

const router = createBrowserRouter([
  {
    path: "*",
    element: (
      <UserProvider>
        <App />
      </UserProvider>
    ),
  },
]);

root.render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <SettingsProvider>
          <RouterProvider router={router} />
        </SettingsProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);
